import React, { useState, useEffect, createRef, useContext, useMemo } from 'react';
import { Redirect } from 'react-router';
import Dropzone, { useDropzone } from 'react-dropzone';
import { withStyles } from '@material-ui/core/styles';
import { Button, CircularProgress, FormControlLabel } from '@material-ui/core';
// import { showNotification } from "react-admin";
import PropTypes from 'prop-types';
import classnames from 'classnames';
import { Autocomplete } from '../Autocomplete';
import { AppContext } from '../../AppContext';
import styles from './DynamicForm.styles';
import { Switch, Checkbox, Date, File, Number, Select, Text, TextArea, List } from './FormInputs';
import { Dialog, DialogActions, DialogContent, DialogTitle, Grid } from '@material-ui/core';
import { TextField } from '@material-ui/core';
import { addressValidation } from '../../services';

import { validateInputs, createErrorObject, createEmptyDataObject, renderInput, flattenObject } from './utils';
import ColoredCheckbox from '../ColoredInputs/ColoredCheckbox';

const MESSAGE_TIMEOUT = 10000;

/**
 * * Details:
 * - the FormLayout is a component that renders the inputs for the CREATE and EDIT forms
 * - the form will handle all state and make the appropriate requests when finished
 * - CREATE: will render all inputs blank
 * - EDIT: will render all inputs with corresponding data provided
 *
 * - onSave: will call parent 'save' function
 *
 */
export const DynamicForm = withStyles(styles)((props) => {
  const {
    classes,
    inputs,
    mode,
    data: dataFromParent,
    onSave,
    hideSubmitButton,
    clearStateAfterSave,
    redirectOnSuccess,
    onDelete,
    dangerouslyUpdateData,
    customActionsPanel,
    onSaveSuccess,
    saveButtonText,
    multiLocation,
    clearLocation,
    validateAddress = false,
    useOriginalAddress = false,
    setUseOriginalAddress,
    setIsVirtual,
  } = props;

  const { dispatch } = useContext(AppContext);

  const [formData, setFormData] = useState(null);
  const [formInputs, setFormInputs] = useState([]);
  const [formErrors, setFormErrors] = useState({}); // or maybe set to null
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState(null);
  const [submitSuccess, setSubmitSuccess] = useState(null);
  const [isLoading, setIsLoading] = useState(false); // loading data from parent
  const [addressValidationModal, showAddressValidationModal] = useState(false);
  const [correctedAddress, setCorrectedAddress] = useState({});
  const [isCorrectedAddressValid, setIsCorrectedAddressValid] = useState(false);

  const canSubmit = useMemo(() => useOriginalAddress && isCorrectedAddressValid, [
    useOriginalAddress,
    isCorrectedAddressValid,
  ]);

  let formHasError = false;

  /**
   * @func handleSave
   * @desc runs when the SAVE button is clicked
   */
  const handleSaveWithValidation = async () => {
    // validate inputs and generate error messages (if any)
    const errorObj = validateInputs(formInputs, formData);
    setFormErrors(errorObj);

    // check if there were any errors
    const formHasError = Object.values(flattenObject(errorObj)).filter((e) => e).length;
    if (formHasError) {
      return setSubmitError('There are form errors... Please change them and try submitting again.');
    }

    let res = await addressValidation(formData);

    let obj = {
      insuredaddress: res.addressLine1,
      insuredcity: res.city,
      insuredstate: res.state,
      insuredzip: res.postalCode,
    };

    if (Object.values(obj).every((value) => value === null)) {
      setIsCorrectedAddressValid(false);
    } else {
      setIsCorrectedAddressValid(true);

      setCorrectedAddress(obj);
    }

    showAddressValidationModal(validateAddress);
  };

  const handleAddressValidationModel = () => {
    showAddressValidationModal(false);
    setCorrectedAddress({});
    setUseOriginalAddress(false);
  };

  /**
   * @func handleSave
   * @desc runs when the SAVE button is clicked - after handleSaveWithAddressValidation
   */
  const handleSave = async () => {
    // // validate inputs and generate error messages (if any)
    // const errorObj = validateInputs(formInputs, formData);
    // setFormErrors(errorObj);

    // // check if there were any errors
    // const formHasError = Object.values(flattenObject(errorObj)).filter(e => e).length;
    // if (formHasError) {
    //   return setSubmitError('There are form errors... Please change them and try submitting again.');
    // }
    const errorObj = validateInputs(formInputs, formData);
    setFormErrors(errorObj);

    let finalObject = !useOriginalAddress ? { ...formData, ...correctedAddress } : formData;

    if ((Object.values(errorObj).every((x) => !x) && mode === 'CREATE') || mode === 'EDIT') {
      setIsSubmitting(true);
      setSubmitError(null);
      setSubmitSuccess(null);
      try {
        const res = await onSave(finalObject);

        if (res) {
          dispatch({
            type: 'SET_SNACKBAR',
            snackbar: {
              isOpen: true,
              message: res.message || 'Saved successfully',
              variant: 'success',
            },
          });
        }

        // clear inputs if mode is CREATE and state should be cleared
        if (clearStateAfterSave && mode === 'CREATE') {
          // create data object from inputs
          const dataObj = createEmptyDataObject(inputs);
          const errorObj = createErrorObject(inputs, dataObj, {});
          setFormErrors(errorObj);
          setFormData(dataObj);
        }

        if (!clearStateAfterSave && !multiLocation) {
          setFormData(res?.model ? res.model : res);
        } else if (multiLocation) {
          formData.insuredaddress = '';
          //formData.insuredphone1 = '';
          //formData.insuredphone2 = '';
          formData.insuredzip = '';
        }

        setSubmitSuccess(true);

        !!onSaveSuccess && onSaveSuccess();
      } catch (error) {
        let errorMessage = 'An Error Occurred.';
        if (error) {
          if (error.message) {
            errorMessage = error.message;
          } else {
            const json = JSON.parse(await error.text());
            const isArray = Array.isArray(json);
            if (json) {
              if (isArray) {
                errorMessage = json.map((error, index) => <p key={index}>{error.description || error}</p>);
              } else if (typeof json === 'object') {
                errorMessage = json.message;
              } else if (typeof json === 'string') {
                errorMessage = json;
              }
            }
          }
        }
        setSubmitError(errorMessage);
      } finally {
        setIsSubmitting(false);
        !!setUseOriginalAddress && setUseOriginalAddress(false);
      }
    }
  };

  /**
   * @func handleDelete
   * @desc runs the onDelete function when the DELETE button is clicked
   */
  const handleDelete = async () => {
    setIsSubmitting(true);
    setSubmitError(null);
    setSubmitSuccess(null);

    try {
      const res = await onDelete(formData);
      setSubmitSuccess('Success!');
      setTimeout(() => setSubmitSuccess(null), MESSAGE_TIMEOUT);

      // clear inputs if mode is CREATE and state should be cleared
      if (clearStateAfterSave && mode === 'CREATE') {
        // create data object from inputs
        const dataObj = createEmptyDataObject(inputs);
        const errorObj = createErrorObject(inputs, dataObj, {});
        setFormErrors(errorObj);
        setFormData(dataObj);
      }
    } catch (err) {
      if (typeof err.text === 'function') {
        const json = JSON.parse(await err.text());
        setSubmitError(json && typeof json === 'object' ? json.Message : 'An error has occurred');
      } else if (err instanceof Array) {
        const error = err[0];
        setSubmitError(`${error.code}: ${error.description}`);
        // showNotification(`${error.code}: ${error.description}`);
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  /**
   * @func resetErrors
   * @desc clear all errors and rebuild (used when a list has an item added or deleted)
   */
  const resetErrors = () => {
    const errorObj = createErrorObject(inputs, formData, {});
    setFormErrors(errorObj);
  };

  /**
   * @func addListItem
   * @desc add a default object to an array of values
   */
  const addListItem = (input, treeTrace, existingData) => {
    // console.log(input)
    const { field, children } = input;
    let tempFormData = Object.assign({}, formData);

    // generate empty data object
    const emptyDataObj = existingData.concat(createEmptyDataObject(children));

    // console.log(emptyDataObj)
    // console.log(tempFormData, treeTrace, field)
    tempFormData = setNestedValue(tempFormData, treeTrace, field, emptyDataObj);
    setFormData(tempFormData);
  };

  /**
   * @func componentDidMount
   * @desc set initial state
   */
  useEffect(() => {
    setFormInputs(inputs);

    // get the existing data if in EDIT mode
    if (mode === 'EDIT' && dataFromParent) {
      setFormData(dataFromParent);
    }

    // start blank
    if (mode === 'CREATE') {
      // create data object from inputs
      const dataObj = createEmptyDataObject(inputs);
      const errorObj = createErrorObject(inputs, dataObj, {});
      setFormErrors(errorObj);
      setFormData(dataObj);
    }
  }, []);

  /**
   * @func componentDidUpdate
   * @desc update data from parent if it is provided
   */
  useEffect(() => {
    const setData = (data) => {
      setFormData(data);
    };

    // set form inputs (may update inputs with new data)
    setFormInputs(inputs);

    // check if there is new data and there isn't any existing data
    if ((dataFromParent && !formData) || dangerouslyUpdateData) {
      setData(dataFromParent);
    }

    if (formData && formInputs && !Object.keys(formErrors).length) {
      const errorObj = createErrorObject(formInputs, formData); // using data provided by the parent
      setFormErrors(errorObj);
    }
  });

  const setNestedValue = (data, treeTrace, key, value) => {
    if (!treeTrace?.length) {
      data[key] = value;
      // console.log(key, value);
    } else {
      const { index, field } = treeTrace[0];

      // get the next piece of the trace
      if (index === null) {
        data[field] = setNestedValue(data[field], treeTrace.slice(1), key, value);
      } else {
        data[field][index] = setNestedValue(data[field][index], treeTrace.slice(1), key, value);
      }
    }
    return data;
  };

  const getNestedValue = (data, treeTrace, key, input) => {
    let returnVal = '';

    if (!treeTrace.length) {
      returnVal = data[key];
    } else {
      const { index, field } = treeTrace[0];

      if (index === null) {
        returnVal = getNestedValue(data[field], treeTrace.slice(1), key);
      } else {
        returnVal = getNestedValue(data[field][index], treeTrace.slice(1), key);
      }
    }

    if (['number', 'string', 'boolean'].includes(typeof returnVal)) {
      return returnVal;
    } else {
      return '';
    }
  };

  /**
   *
   * @param {*} field
   * @param {*} value
   * @param { Array } treeTrace
   * @param {*} parentOnChange
   */
  const onChange = async (field, value, treeTrace, parentOnChange, indexInList) => {
    let tempFormData = Object.assign({}, formData);
    // console.log(tempFormData, field, value, treeTrace, parentOnChange);

    // console.log(field, treeTrace, value);

    // TODO.babyjay: get rid of this 'else' and always call 'setNestedValue'???
    tempFormData = setNestedValue(tempFormData, treeTrace, field, value);
    // if (!treeTrace.length) {
    //   tempFormData[field] = value;
    // } else {
    //   tempFormData = setNestedValue(tempFormData, treeTrace, field, value);
    // }
    // console.log(tempFormData)
    setFormData(tempFormData);

    // do not require insuredphone #1 for virtual orders
    if (field === 'reportcode') {
      if (value.includes('UEV')) {
        setIsVirtual(true);
      } else {
        setIsVirtual(false);
      }
    }

    if (parentOnChange) {
      parentOnChange(value, indexInList);
    }
  };

  const handleUseOriginalAddressOption = (ev) => {
    setUseOriginalAddress(ev.target.checked);
  };

  useEffect(() => {
    if (formData) {
      formData.insuredcity = '';
      formData.insuredstate = '';
    }
  }, [clearLocation]);

  return (
    <>
      <div>
        {/* show a spinner while data is loading from the api */}
        {isLoading && (
          <div
            style={{
              height: '100%',
              display: 'flex',
              flexDirection: 'column',
              justifyContent: 'center',
              alignItems: 'center',
            }}
          >
            <CircularProgress style={{ fill: '#222', color: '#222' }} size={150} />
          </div>
        )}

        {!isLoading && !formData && (
          <div
            style={{
              flex: 1,
              // height: "100%",
              display: 'flex',
              flexDirection: 'column',
              justifyContent: 'center',
              alignItems: 'center',
            }}
          >
            <CircularProgress style={{ fill: '#222', color: '#222' }} size={150} />
          </div>
        )}

        {/* rendering inputs when page is loaded */}
        {!isLoading && formData && (
          <div className={classes.formContainer}>
            {!!Object.keys(formErrors).length &&
              formInputs.map((i, idx) => {
                // hide inputs that shouldn't be shown on CREATE (most likely will be generated by the back end)
                if (mode === 'CREATE' && i.hideOnCreate) {
                  return <></>;
                }

                return renderInput({
                  i,
                  treeTrace: [],
                  errors: formErrors,
                  data: formData,
                  extraIndexForKey: idx,
                  onChange,
                  mode,
                  resetErrors,
                  addListItem,
                });
              })}
            {!hideSubmitButton && (
              <div
                style={{
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'flex-start',
                  marginTop: '12px',
                }}
              >
                <Button
                  disabled={isSubmitting}
                  style={{
                    // marginLeft: 10,
                    backgroundColor: '#17c80d',
                    color: '#fff',
                  }}
                  variant="contained"
                  onClick={validateAddress ? handleSaveWithValidation : handleSave}
                >
                  {isSubmitting ? (
                    <CircularProgress style={{ fill: '#fff', color: '#fff' }} size={25} />
                  ) : (
                    saveButtonText
                  )}
                </Button>
                {onDelete && (
                  <Button
                    disabled={isSubmitting}
                    style={{
                      marginLeft: 10,
                      backgroundColor: 'red',
                      color: '#fff',
                    }}
                    variant="contained"
                    onClick={handleDelete}
                  >
                    {isSubmitting ? <CircularProgress style={{ fill: '#fff', color: '#fff' }} size={25} /> : 'DELETE'}
                  </Button>
                )}

                {submitError && <p className={classes.textLabelError}>{submitError}</p>}
                {submitSuccess && redirectOnSuccess && <Redirect to={redirectOnSuccess} />}
                {submitSuccess && <p className={classes.textLabelSuccess}>{submitSuccess}</p>}
              </div>
            )}

            {!!customActionsPanel &&
              customActionsPanel({
                isSubmitting,
                onSave: handleSave,
                onDelete: handleDelete,
                submitError,
                submitSuccess,
              })}
          </div>
        )}
      </div>
      {validateAddress ? (
        <Dialog
          maxWidth="md"
          fullWidth={true}
          open={addressValidationModal}
          onClose={handleAddressValidationModel}
          aria-labelledby="form-dialog-title"
        >
          <DialogTitle id="form-dialog-title">Corrected Address</DialogTitle>

          <DialogContent>
            <Grid container spacing={2}>
              <Grid xs={12} style={{ paddingLeft: 10, paddingRight: 10 }}>
                A valid corrected address is found from the original address entered. If you need to override the
                corrected address, use the original address and confirm. Otherwise, the corrected address will be used.
              </Grid>
              <Grid item xs={6} style={{ display: 'flex', flexDirection: 'column', gap: 15 }}>
                Original Address:
                <TextField
                  type="text"
                  label="Insured Address"
                  variant="outlined"
                  value={formData?.insuredaddress}
                  disabled
                />
                <TextField
                  type="text"
                  label="Insured City"
                  value={
                    formInputs[0]?.children[0]?.children.find((x) => x.field == 'insuredcity')?.placeholder ??
                    formData?.insuredcity
                  }
                  variant="outlined"
                  disabled
                />
                <TextField
                  type="text"
                  label="Insured State"
                  value={
                    formInputs[0]?.children[0]?.children.find((x) => x.field == 'insuredstate')?.placeholder ??
                    formData?.insuredstate
                  }
                  variant="outlined"
                  disabled
                />
                <TextField type="text" label="Insured Zip" value={formData?.insuredzip} variant="outlined" disabled />
              </Grid>
              <Grid item xs={6} style={{ display: 'flex', flexDirection: 'column', gap: 15 }}>
                Corrected Address:
                <TextField
                  type="text"
                  label="Insured Address"
                  value={correctedAddress?.insuredaddress}
                  variant="outlined"
                  disabled
                />
                <TextField
                  type="text"
                  label="Insured City"
                  value={correctedAddress?.insuredcity}
                  variant="outlined"
                  disabled
                />
                <TextField
                  type="text"
                  label="Insured State"
                  value={correctedAddress?.insuredstate}
                  variant="outlined"
                  disabled
                />
                <TextField
                  type="text"
                  label="Insured Zip"
                  value={correctedAddress?.insuredzip}
                  variant="outlined"
                  disabled
                />
              </Grid>
              {!isCorrectedAddressValid && (
                <Grid xs={12} style={{ paddingLeft: 10, paddingRight: 10, color: 'red' }}>
                  The address entered could not be found! <br></br>
                  Please make sure you have entered the appropriate fields, Or check Use Original Address and click the
                  Confirm button.
                </Grid>
              )}
            </Grid>
          </DialogContent>
          <DialogActions className={classes.dialogActions}>
            <FormControlLabel
              control={<ColoredCheckbox onChange={handleUseOriginalAddressOption} />}
              label="Use Original Address"
            />

            <Button color="secondary" onClick={handleAddressValidationModel}>
              Cancel
            </Button>
            <Button
              variant="contained"
              onClick={handleSave}
              style={{
                // marginLeft: 10,
                backgroundColor: !useOriginalAddress ? (!isCorrectedAddressValid ? 'gray' : '#17c80d') : '#17c80d',
                color: '#fff',
              }}
              disabled={!useOriginalAddress ? !isCorrectedAddressValid : false}
            >
              {isSubmitting ? <CircularProgress style={{ fill: '#fff', color: '#fff' }} size={25} /> : 'Confirm'}
            </Button>
          </DialogActions>
        </Dialog>
      ) : (
        <></>
      )}
    </>
  );
});

DynamicForm.propTypes = {
  inputs: PropTypes.array.isRequired,
  mode: PropTypes.oneOf(['EDIT', 'CREATE']),

  // Pass in `data` and `handleTextChange` together if there is a parent component handling state (tabs) and mode is EDIT
  data: PropTypes.object,
  // handleTextChange: PropTypes.func,
  onSave: PropTypes.func,
  hideSubmitButton: PropTypes.bool,
  clearStateAfterSave: PropTypes.bool,
  onDelete: PropTypes.func,
  dangerouslyUpdateData: PropTypes.bool,
  customActionsPanel: PropTypes.function,
  onSaveSuccess: PropTypes.function,
  saveButtonText: PropTypes.string,
  multiLocation: PropTypes.bool,
  // dataId: PropTypes.string,
  // getData: PropTypes.func,

  // dataId: (props, propName, compName) => {
  //   if (
  //     props.mode === 'EDIT'
  //     && (props[propName] === undefined || typeof(props[propName] !== PropTypes.string))) {
  //     return new Error( 'Please provide a data ID via `dataId`' );
  //   }
  // },

  // getData: (props, propName, compName) => {
  //   if (props.mode === 'EDIT' && props[propName] === undefined || typeof(props[propName] !== 'function')) {
  //     return new Error( 'Please provide a getter function  `dataId`' );
  //   }
  // },

  // putData: PropTypes.func,
  // postData: PropTypes.func,
};

DynamicForm.defaultProps = {
  inputs: [],
  mode: 'CREATE',
  hideSubmitButton: false,
  clearStateAfterSave: false,
  redirectOnSuccess: false,
  dangerouslyUpdateData: false,
  customActionsPanel: null,
  onSaveSuccess: null,
  saveButtonText: 'SAVE',
  multiLocation: false,
};

// export default withStyles(styles)(FormLayout);

/**
 * * INSTRUCTIONS FOR THIS COMPONENT:
 *
 * 1. Pass in an array of inputs with the corresponding properties
 * 2. Pass in a form mode to start editing or creating
 * 3. Pass in a save function to pass to new data to
 *
 * - input props:
 *    - inputType: text, number, hidden, etc
 *    - field: the JSON key value
 *    - label: the title of the input visible to the user
 *    - validate: array of functions to validate the input
 *      * each function should return null if valid or an error string if invalid
 *      * uses a reduce function and checks the final result
 *    - disabledOnEdit: boolean to disable an input when editng an entry (for non-editable properties)
 *    - hideOnCreate: boolean to hide input when creating an entry (e.g. a primary key that is auto generated)
 *    - *** any other input prop will be passed down to the input by the spread operator
 */
